import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import { getRecordMetadataColumns } from "@/components/tables/recordMetadataColumns";
import { useTranslation } from "react-i18next";
import {
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Switch,
  Tooltip,
} from "@mui/material";
import { api, apiRequest } from "@/helper/api";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@/components/state/store";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import Modal from "@/components/modal";
import GenericNotification from "@/components/notification/genericNotification";
import RestrictedPage from "@/consumer/restrictedPage";
import { useNavigate } from "react-router-dom";
import PaymentsIcon from "@mui/icons-material/Payments";
import MapsHomeWorkIcon from "@mui/icons-material/MapsHomeWork";
import styles from "@/routes/admin/companyList.module.css";
import { KaerIcon } from "@/icons/kaerIcon";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import DropDown from "@/components/forms/inputs/dropDown";
import DeleteIcon from "@mui/icons-material/Delete";
import UndoIcon from "@mui/icons-material/Undo";
import { LinearProgress } from "@mui/material";
import NavigateToSubjectOverviewLink from "@/components/subjectOverview/navigateToSubjectOverviewLink";
import FileUploadInput from "@/components/forms/inputs/upload";
import TodoCreateEditModal from "@/components/todos/todoCreateEditModal";
import CompanyListRowSelectionActionButtons from "@/routes/admin/companyListRowSelectionActionButtons";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GroupIcon from "@mui/icons-material/Group";
import CompanyHierarchyModal from "@/components/company/companyHierarchyModal";
import CreateDocumentModal from "@/components/document/createDocumentModal";

import { startBackgroundTask } from "@/components/backgroundTasks/startBackgroundTask";
import BackgroundTaskStartedNotice from "@/components/backgroundTasks/backgroundTaskStartedNotice";
import CreateTrackedServiceModal from "@/components/trackedService/createTrackedServiceModal";
import CompanyBatchUploadModal from "@/components/files/company/companyBatchUploadModal";
import CompanyBatchEmailModal from "@/components/company/batchCreate/companyBatchEmailModal";
import CompanyAdditionalGroupAssignments, {
  AdditionalGroupAssignment,
} from "@/components/company/companyAdditionalGroupAssignments";
import CompaniesWithoutQueuedMailList from "@/components/company/batchCreate/companiesWithoutQueuedMailList";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import { useCompanySetupCompletion } from "@/components/company/useCompanySetupCompletion";
import type { FileUploadRequestOptions } from "@/helper/customHooks/useFileUploadProgress";
import { companyEntityAdapter } from "@/components/company/companyEntityAdapter";
import MailPreferencesDialog from "@/components/mailTemplate/mailPreferencesDialog";
import Restricted from "@/consumer/restricted";
import EmailIcon from "@mui/icons-material/Email";

const INITIAL_SHOW_ALL_COMPANIES = false;
const INITIAL_SHOW_PARENT_COMPANIES_ONLY = false;

const CompanyList: FC = () => {
  const { t } = useTranslation();
  usePageHeading(t("company"));
  const { handleError, errorModal } = useErrorModal();
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [mailPreferenceSubject, setMailPreferenceSubject] = useState<
    string | null
  >(null);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [subjectId, setSubjectId] = useState<any>("");
  const [demoCompanies, setDemoCompanies] = useState<any[]>([]);
  const [demoCompanyId, setDemoCompanyId] = useState<string | null>(null);
  const [refreshTable, setRefreshTable] = useState<boolean>(false);
  const [modalUploadOpen, setModalUploadOpen] = useState<boolean>(false);
  const [resetRowSelection, setResetRowSeletion] = useState<boolean>(false);
  const [showAllCompanies, setShowAllCompanies] = useState<boolean>(
    INITIAL_SHOW_ALL_COMPANIES,
  );
  const [showParentCompaniesOnly, setShowParentCompaniesOnly] =
    useState<boolean>(INITIAL_SHOW_PARENT_COMPANIES_ONLY);
  const [modalTodoOpen, setModalTodoOpen] = useState<boolean>(false);
  const [selectedCompaniesForTodo, setSelectedCompaniesForTodo] = useState<any>(
    [],
  );
  const [modalEmailOpen, setModalEmailOpen] = useState<boolean>(false);

  const [selectedRowsForEmail, setSelectedRowsForEmail] = useState<any[]>([]);
  const [emailResults, setEmailResults] = useState<any>(null);
  const [emailResultsModalOpen, setEmailResultsModalOpen] =
    useState<boolean>(false);

  const [hierarchyModalOpen, setHierarchyModalOpen] = useState<boolean>(false);
  const [selectedCompanyForHierarchy, setSelectedCompanyForHierarchy] =
    useState<{
      subjectId: string;
      companyId: string;
    } | null>(null);
  const [csvUploadModalOpen, setCsvUploadModalOpen] = useState<boolean>(false);
  const [csvUploadInProgress, setCsvUploadInProgress] = useState(false);
  const [companyAdditionalGroups, setCompanyAdditionalGroups] = useState<{
    id: string;
    assignments: AdditionalGroupAssignment[];
  } | null>(null);
  const [entries, setEntries] = useState({
    created: [],
    updated: [],
    skipped: [],
  });
  const [entriesModalOpen, setEntriesModalOpen] = useState<boolean>(false);
  const [createDocumentModalOpen, setCreateDocumentModalOpen] =
    useState<boolean>(false);
  const [createTrackedServiceModalOpen, setCreateTrackedServiceModalOpen] =
    useState<boolean>(false);
  const selectedRowsRef = useRef<any[]>([]);
  const handleRowSelectionChange = useCallback((rows: any[]) => {
    selectedRowsRef.current = rows || [];
  }, []);
  const handleRowSelectionCleared = useCallback(() => {
    selectedRowsRef.current = [];
  }, []);

  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();
  const navigate = useNavigate();
  const { showConfirmationModalAsync, showConfirmationModal } =
    useConfirmationModal();
  const dispatch = useDispatch();

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // const folderId = "11111111-1111-1111-1111-888888888888";

  const user = useSelector((state: RootState) => state.login);
  const resetStateOnCloseNotification = () => {
    setShowNotification(false);
    setNotificationMessage("");
  };

  useEffect(() => {
    const additionalUrlParameters: any = {};
    additionalUrlParameters["demo"] = true;
    api.genericApiRequest({
      method: "get",
      entity: "company",
      parametersToRender: additionalUrlParameters,
      successHandler: (res: any) => {
        const data = res.data.results;
        const tempCompanies = data.map((company: any) => {
          return { value: company.id, label: company.name };
        });
        setDemoCompanies(tempCompanies);
      },
    });
  }, []);

  const handleDeleteDemoCompany = async () => {
    const isConfirmed = await showConfirmationModalAsync(
      t("confirm_reset_demo"),
    );

    if (!isConfirmed) {
      return;
    }

    const additionalRouteParts: any = {};
    additionalRouteParts["reset_demo_company"] = "/";
    const result = {};
    try {
      await new Promise<void>((resolve, reject) => {
        api.genericApiRequest({
          entity: "company",
          method: "put",
          additionalRouteParts: additionalRouteParts,
          entityId: demoCompanyId,
          submitData: result,
          successHandler: () => {
            resolve();
          },
          failHandler: (error: any) => {
            reject(error);
          },
        });
      });

      setShowNotification(true);
      setNotificationMessage(t("reset_successful"));
      setNotificationVariant("success");
      setDemoCompanyId(null);
    } catch (error) {
      handleError(error);
    }
  };
  const handleCompanyActiveStatus = async (row: any, status: any) => {
    let message = t("mark_for_delete_confirm", { name: row.name });

    if (status) {
      message = t("mark_for_undelete_confirm", { name: row.name });
    }

    const isConfirmed = await showConfirmationModalAsync(message);

    if (!isConfirmed) {
      return;
    }

    const result = { is_active: status };

    api.genericApiRequest({
      entity: "company",
      method: "put",

      successHandler: (res: any) => {
        const data = res.data;
        if (data.is_active === true) {
          setNotificationMessage(t("company_reactivate_success"));
        } else {
          setNotificationMessage(t("mark_for_delete_success"));
        }
        setShowNotification(true);
        setNotificationVariant("success");
      },
      failHandler: (error: any) => {
        handleError(error);
      },
      entityId: row.id,
      submitData: result,
    });
  };

  let disallowDelete = true;
  if (user.email == "admin@kaer.de") {
    disallowDelete = false;
  }

  const handleNavigate = (row: any, target: string) => {
    switch (target) {
      case "buildings":
        navigate(`/buildings/${row.subject.id}`);
        break;
      case "trackedservice":
        navigate(`/trackedservice/company/${row.subject.id}`);
        break;
      case "actionchain":
        navigate(`/actionchain/company/${row.subject.id}`);
    }
  };

  const setSubjectIdForBuildings = (row: any) => {
    setSubjectId(row.subject.id);
  };

  const customRowActions = (row: any) => {
    return (
      <>
        <Restricted permissionRequired="change_mailtemplate">
          <Tooltip title={t("mail_preferences_title")}>
            <GenericButton
              variant="icon"
              color="iconBase"
              onClick={() =>
                setMailPreferenceSubject(
                  row.original.subject?.id ?? row.original.subject_id
                )
              }
            >
              <EmailIcon />
            </GenericButton>
          </Tooltip>
        </Restricted>
        <Tooltip
          arrow
          placement="top"
          title={t("additional_groups_for_company_users")}
        >
          <GenericButton
            variant="icon"
            color="iconBase"
            onClick={() =>
              setCompanyAdditionalGroups({
                id: row.original.id,
                assignments: row.original.additional_group_assignments || [],
              })
            }
          >
            <GroupIcon />
          </GenericButton>
        </Tooltip>
        <Tooltip
          arrow
          placement="top"
          enterDelay={1000}
          enterNextDelay={250}
          title={
            row.original.is_active
              ? t("mark_for_delete")
              : t("company_set_active")
          }
        >
          <GenericButton
            variant="icon"
            color="error"
            onClick={() => {
              handleCompanyActiveStatus(row.original, !row.original.is_active);
            }}
          >
            {row.original.is_active ? <DeleteIcon /> : <UndoIcon />}
          </GenericButton>
        </Tooltip>

        <Tooltip
          arrow
          placement="top"
          enterDelay={1000}
          enterNextDelay={250}
          title={t("buildings")}
        >
          <GenericButton
            onClick={() => handleNavigate(row.original, "buildings")}
            variant="icon"
            color="iconBase"
          >
            <MapsHomeWorkIcon />
          </GenericButton>
        </Tooltip>

        <Tooltip
          arrow
          placement="top"
          enterDelay={1000}
          enterNextDelay={250}
          title={t("trackedservice")}
        >
          <GenericButton
            variant="icon"
            color="iconBase"
            onClick={() => handleNavigate(row.original, "trackedservice")}
          >
            <PaymentsIcon />
          </GenericButton>
        </Tooltip>
        <Tooltip
          arrow
          placement="top"
          enterDelay={1000}
          enterNextDelay={250}
          title={t("actionchain_active_chains")}
        >
          <GenericButton
            onClick={() => handleNavigate(row.original, "actionchain")}
            variant="icon"
            color="iconBase"
          >
            <KaerIcon icon="actionChain" color="#707070" />
          </GenericButton>
        </Tooltip>
        <Tooltip
          arrow
          placement="top"
          enterDelay={1000}
          enterNextDelay={250}
          title={t("company_hierarchy")}
        >
          <GenericButton
            onClick={() => {
              setSelectedCompanyForHierarchy({
                subjectId: row.original.subject?.id || row.original.subject_id,
                companyId: row.original.id,
              });
              setHierarchyModalOpen(true);
            }}
            variant="icon"
            color="iconBase"
          >
            <KaerIcon icon="companyHierarchy" color="#707070" />
          </GenericButton>
        </Tooltip>
      </>
    );
  };

  const handleDemoCompanyChange = (selectedOption: any) => {
    if (selectedOption !== undefined) {
      setDemoCompanyId(selectedOption?.value);
    }
  };
  const handleDemoCompyResetInput = () => {
    setDemoCompanyId(null);
  };

  useEffect(() => {
    if (resetRowSelection) {
      selectedRowsRef.current = [];
    }
  }, [resetRowSelection]);

  const handleSendEmailModalOpen = (selectedRows?: any[]) => {
    const rowsToUse = selectedRows || selectedRowsRef.current;
    setSelectedRowsForEmail(rowsToUse);

    setModalEmailOpen(true);
  };

  const handleSendEmailModalClose = () => {
    setModalEmailOpen(false);
    setSelectedRowsForEmail([]);
    setResetRowSeletion((prev: any) => !prev);
  };

  const handleCreateDocumentModalOpen = (selectedRows?: any[]) => {
    const rowsToUse = selectedRows || selectedRowsRef.current;
    setSelectedRowsForEmail(rowsToUse);

    setCreateDocumentModalOpen(true);
  };
  const handleCreateTrackedServiceModalOpen = (selectedRows?: any[]) => {
    const rowsToUse = selectedRows || selectedRowsRef.current;
    setSelectedRowsForEmail(rowsToUse);

    setCreateTrackedServiceModalOpen(true);
  };
  const handleBatchUploadModalClose = () => {
    setModalUploadOpen(false);
    setSelectedRowsForEmail([]);
    setResetRowSeletion((prev: any) => !prev);
  };

  const handleCreateDocumentModalClose = () => {
    setCreateDocumentModalOpen(false);
    setSelectedRowsForEmail([]);
    setResetRowSeletion((prev: any) => !prev);
  };
  const handleCreateTrackedServiceModalClose = () => {
    setCreateTrackedServiceModalOpen(false);
    setSelectedRowsForEmail([]);
    setResetRowSeletion((prev: any) => !prev);
  };

  const handleBatachUpload = async (
    data: any,
    { onUploadProgress }: FileUploadRequestOptions,
  ) => {
    try {
      const formData = new FormData();
      if (data.file_set && data.file_set.length > 0) {
        data.file_set.forEach((file: File) => {
          formData.append("file_set", file);
        });
      }

      formData.append(
        "company_subject_set",
        JSON.stringify(data.company_subject_set),
      );

      formData.append("folder_id", data.folder_id);
      formData.append("is_exclusion_list", String(data.is_exclusion_list));
      formData.append("parent_only", String(data.parents_only));
      formData.append("include_children", String(data.include_children));
      // formData.append("use_manager_subjects", "false");

      const response: any = await new Promise((resolve, reject) => {
        api.genericApiRequest({
          method: "post",
          entity: "file",
          additionalRouteParts: { multi_upload: "/" },
          successHandler: (res: any) => resolve(res),
          failHandler: (error: any) => reject(error),
          submitData: formData,
          config: {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress,
          },
          disableGlobalSpinner: true,
        });
      });
      const taskTrackingId = response?.data?.task_tracking_id;

      if (taskTrackingId) {
        const taskData = {
          id: taskTrackingId,
          description: t("batch_upload_taskstatus"),
          kind: "batch_upload" as const,
        };

        startBackgroundTask(dispatch, taskData);

        handleBatchUploadModalClose();

        showConfirmationModal(
          <BackgroundTaskStartedNotice
            message={t("batch_upload_started_infotext")}
            hint={t("create_document_task_waiting_infotext")}
          />,
          {
            title: t("task_created"),
            disableCancelButton: true,
          },
        );
      } else {
        throw new Error("No task_tracking_id returned from multi_upload");
      }
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  const handleCreateDocument = async (data: any) => {
    try {
      const response: any = await new Promise((resolve, reject) => {
        api.genericApiRequest({
          method: "post",
          entity: "file",
          additionalRouteParts: { render_document: "/" },
          successHandler: (res: any) => resolve(res),
          failHandler: (error: any) => reject(error),
          submitData: data,
        });
      });

      const taskTrackingId = response?.data?.task_tracking_id;
      if (taskTrackingId) {
        const taskData = {
          id: taskTrackingId,
          description: t("create_batchupload_taskstatus"),
          kind: "batch_document" as const,
        };
        startBackgroundTask(dispatch, taskData);

        handleCreateDocumentModalClose();

        showConfirmationModal(
          <BackgroundTaskStartedNotice
            message={t("create_batchupload_infotext")}
            hint={t("create_document_task_waiting_infotext")}
          />,
          {
            title: t("task_created"),
            disableCancelButton: true,
          },
        );
      } else {
        throw new Error("No task_tracking_id returned from render_document");
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleCreatetrackedServiceEntry = async (data: any) => {
    try {
      await new Promise((resolve, reject) => {
        api.genericApiRequest({
          method: "post",
          entity: "trackedService",
          successHandler: (res: any) => resolve(res),
          failHandler: (error: any) => reject(error),
          submitData: data,
        });
      });
      handleCreateTrackedServiceModalClose();
      setNotificationMessage(t("successfully_created"));
      setNotificationVariant("success");
      setShowNotification(true);
    } catch (error: any) {
      handleError(error);
    }
  };

  const handleSendEmail = async (data: any) => {
    const { send_to_customers, ...submitData } = data;

    const additionalRouteParts: any = send_to_customers
      ? { send_to_customers: "/" }
      : { send_to_managers: "/" };

    try {
      const response: any = await new Promise((resolve, reject) => {
        api.genericApiRequest({
          entity: "mail",
          method: "post",
          additionalRouteParts: additionalRouteParts,
          submitData: submitData,
          successHandler: (res: any) => {
            resolve(res);
          },
          failHandler: (error: any) => {
            reject(error);
          },
        });
      });

      setEmailResults(response.data);
      setEmailResultsModalOpen(true);
      handleSendEmailModalClose();
    } catch (error) {
      handleError(error);
    }
  };

  const handleCloseEmailResultsModal = () => {
    setEmailResultsModalOpen(false);
    setEmailResults(null);
  };
  const emailResultsModalContent = emailResults ? (
    <div className={styles.entriesModalContainer}>
      <section className={styles.entriesSection}>
        <p className={styles.boldText}>
          {t("number_of_recipients_or_managers")}:
        </p>
        <span>{emailResults.total_recipients}</span>
      </section>
      <section className={styles.entriesSection}>
        <p className={styles.boldText}>{t("status")}:</p>
        <span className={styles.statusText}>
          {t("mails_successfully_queued")}
          <CheckCircleIcon color="success" />
        </span>
      </section>
      {emailResults.batch_id && (
        <section className={styles.entriesSection}>
          <p className={styles.boldText}>{t("email_results_check_status")}</p>
          <GenericButton
            variant="outlined"
            color="tertiary"
            onClick={() => {
              window.open(
                `/settings/emailstatus/${emailResults.batch_id}`,
                "_blank",
                "noopener,noreferrer",
              );
            }}
          >
            {t("status")}
          </GenericButton>
        </section>
      )}
      <CompaniesWithoutQueuedMailList
        companies={emailResults.companies_without_queued_mail ?? []}
      />
    </div>
  ) : null;

  const customTopToolBarActions = () => {
    return (
      <>
        <div className={styles.customActionButtonWrapper}>
          <GenericButton onClick={() => setCsvUploadModalOpen(true)}>
            {t("csv_import")}
          </GenericButton>
          <GenericButton onClick={downloadExampleCsv}>
            {t("csv_import_sample_file")}
          </GenericButton>
        </div>
        <FormControlLabel
          label={
            showAllCompanies
              ? t("company_show_all")
              : t("company_show_active_only")
          }
          control={
            <Switch
              checked={showAllCompanies}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setShowAllCompanies(event.target.checked)
              }
              sx={{
                "& .MuiSwitch-switchBase": {
                  color: "#8c1ec8",
                  "& + .MuiSwitch-track": {
                    backgroundColor: "#8c1ec8",
                  },
                },
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#ff6428",
                  "& + .MuiSwitch-track": {
                    backgroundColor: "#ff6428",
                  },
                },
              }}
            />
          }
        />
        <FormControlLabel
          label={
            showParentCompaniesOnly
              ? t("company_show_parent_only")
              : t("company_show_all_hierarchy")
          }
          control={
            <Switch
              checked={showParentCompaniesOnly}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setShowParentCompaniesOnly(event.target.checked)
              }
              sx={{
                "& .MuiSwitch-switchBase": {
                  color: "#8c1ec8",
                  "& + .MuiSwitch-track": {
                    backgroundColor: "#8c1ec8",
                  },
                },
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#ff6428",
                  "& + .MuiSwitch-track": {
                    backgroundColor: "#ff6428",
                  },
                },
              }}
            />
          }
        />
      </>
    );
  };

  const handleTwoFactor = async (event: any, row: any) => {
    const newChecked = event.target.checked;
    const companyID = row.id;
    const name = row.name;

    const confirmationMessage = event.target.checked
      ? t("2fa_activation_company_admin", { name: name })
      : t("2fa_deactivation_company_admin", { name: name });

    const isConfirmed = await showConfirmationModalAsync(confirmationMessage, {
      disableConfirmFor: 3000,
    });

    if (!isConfirmed) return;

    api.genericApiRequest({
      method: "put",
      entity: "company",
      entityId: companyID,
      submitData: { requires_two_factor: newChecked },
      successHandler: () => {
        setNotificationMessage(t("successfully_saved"));
        setNotificationVariant("success");
        setShowNotification(true);
        setRefreshTable((prev: any) => !prev);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  };
  const { completeSetup: handleDraftMode, setupIncompleteModal } = useCompanySetupCompletion({
    onSuccess: () => {
      setNotificationMessage(t("company_setup_success"));
      setNotificationVariant("success");
      setShowNotification(true);
      setRefreshTable((previous) => !previous);
    },
    onError: handleError,
  });

  const handleBatchUploadModalOpen = (rows: any) => {
    const rowsToUse = rows || selectedRowsRef.current;
    setSelectedRowsForEmail(rowsToUse);
    setModalUploadOpen(true);
  };

  const handleBatchTodoModalOpen = (rows: any) => {
    const rowsToUse = rows || selectedRowsRef.current;
    const subjectIds = rows.map((row: any) => row.subject_id);
    setSelectedCompaniesForTodo(subjectIds);
    setSelectedRowsForEmail(rowsToUse);

    setModalTodoOpen(true);
  };

  const additionalTopToolBarButtons = (selectedRows: any) => {
    return (
      <CompanyListRowSelectionActionButtons
        selectedRows={selectedRows || []}
        onSelectionChange={handleRowSelectionChange}
        onSelectionCleared={handleRowSelectionCleared}
        handleBatchUploadModalOpen={handleBatchUploadModalOpen}
        handleBatchTodoModalOpen={handleBatchTodoModalOpen}
        handleSendEmailModalOpen={handleSendEmailModalOpen}
        handleCreateDocumentModalOpen={handleCreateDocumentModalOpen}
        handleCreateTrackedServiceModalOpen={
          handleCreateTrackedServiceModalOpen
        }
      />
    );
  };

  const isRowSelectable = (row: any) => {
    return row.original.is_active === true;
  };

  const downloadExampleCsv = async () => {
    try {
      const response = await fetch("/api/v1/company/example_csv/", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const result = await response.json();
        const errorContent = result
          ? result
          : t("error_downloading_example_csv");
        throw errorContent;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = t("example_company_import_csv_filename");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      handleError(error);
      setNotificationMessage(t("download_failed"));
      setNotificationVariant("error");
      setShowNotification(true);
    }
  };

  const handleCsvUpload = async (
    files: File[],
    { onUploadProgress }: FileUploadRequestOptions,
  ) => {
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      const response = await apiRequest<Record<string, any>>({
        entity: "company",
        additionalRouteParts: { create_from_csv: "/" },
        method: "post",
        submitData: formData,
        shouldNotImpersonate: true,
        disableGlobalSpinner: true,
        config: {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress,
        },
      });
      const result = response.data;

      setEntries({
        created: result["created entries"] || [],
        updated: result["updated entries"] || [],
        skipped: result["skipped entries"] || [],
      });

      setEntriesModalOpen(true);
      setNotificationMessage(t("upload_successful"));
      setNotificationVariant("success");
      setShowNotification(true);
      setRefreshTable((prev: any) => !prev);
    }
  };

  const getCsvUploadErrorMessages = (entry: any): string => {
    if (entry?.fields && typeof entry.fields === "object") {
      const allErrors: string[] = [];
      Object.keys(entry.fields).forEach((fieldName) => {
        const fieldErrors = entry.fields[fieldName];
        if (Array.isArray(fieldErrors)) {
          allErrors.push(...fieldErrors);
        } else if (fieldErrors) {
          allErrors.push(String(fieldErrors));
        }
      });
      if (allErrors.length > 0) {
        return allErrors.join("; ");
      }
    }
    if (entry?.reason) {
      return entry.reason;
    }
    return t("unknown_reason");
  };

  const entriesModalContent = (
    <div className={styles.entriesModalContainer}>
      <section className={styles.entriesSection}>
        <p className={styles.boldText}>{t("created_entries")}:</p>
        <span>{entries?.created?.length || 0}</span>
      </section>
      <section className={styles.entriesSection}>
        <p className={styles.boldText}>{t("updated_entries")}:</p>
        <span>{entries?.updated?.length || 0}</span>
      </section>
      <section className={styles.entriesSectionColumn}>
        <p className={styles.incorrectEntriesText}>{t("incorrect_entries")}:</p>
        <List>
          {entries?.skipped?.map((entry: any, index: number) => (
            <ListItem key={index} className={styles.errorListItem}>
              <ListItemText
                primary={entry?.name || entry?.email || t("unknown_entry")}
                secondary={getCsvUploadErrorMessages(entry)}
                secondaryTypographyProps={{ color: "red" }}
              />
            </ListItem>
          ))}
        </List>
      </section>
    </div>
  );

  const customTopToolBarAction = () => {
    return (
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <section style={{ width: "25vw" }}>
          <DropDown
            options={demoCompanies}
            onChange={handleDemoCompanyChange}
            isClearable
            resetInput={handleDemoCompyResetInput}
            wrapperStyle={{ width: "100%" }}
            placeholder={t("choose_demo_company")}
          />
        </section>
        {demoCompanyId !== null && demoCompanyId !== undefined && (
          <GenericButton
            variant="outlined"
            color="tertiary"
            onClick={() => handleDeleteDemoCompany()}
          >
            {t("reset")}
          </GenericButton>
        )}
      </div>
    );
  };

  const additionalUrlParameter = useMemo(
    () => ({
      subject: "true",
      ...(showAllCompanies ? { include_deleted: true } : {}),
      ...(showParentCompaniesOnly ? { parent__isnull: true } : {}),
    }),
    [showAllCompanies, showParentCompaniesOnly],
  );

  const handleResetAllFilters = useCallback(() => {
    setShowAllCompanies(INITIAL_SHOW_ALL_COMPANIES);
    setShowParentCompaniesOnly(INITIAL_SHOW_PARENT_COMPANIES_ONLY);
  }, []);

  return (
    <RestrictedPage permissionRequired="frontend_view_company">
      {mailPreferenceSubject && (
        <MailPreferencesDialog
          key={mailPreferenceSubject}
          subjectId={mailPreferenceSubject}
          onClose={() => setMailPreferenceSubject(null)}
        />
      )}
      <GenericCrudTable
        allowColumnFiltering={true}
        showColumnFilterbyDefault={true}
        resetRowSelection={resetRowSelection}
        enableRowSelection={isRowSelectable}
        subjectIdForBuildings={subjectId}
        onOpenEdit={setSubjectIdForBuildings}
        entity={"company"}
        entityAdapter={companyEntityAdapter}
        refreshTable={refreshTable}
        additionalUrlParameter={additionalUrlParameter}
        hideDeleteButtonForRows={disallowDelete}
        customRowActions={customRowActions}
        additionalCustomTopToolbarElements={customTopToolBarActions}
        additionalRowsPerPageOptions={[200, 400, 600]}
        additionalCustomTopToolbarElementsForRowSelection={
          additionalTopToolBarButtons
        }
        customTopToolBarAction={customTopToolBarAction}
        hasAdditionalFiltersToReset={
          showAllCompanies !== INITIAL_SHOW_ALL_COMPANIES ||
          showParentCompaniesOnly !== INITIAL_SHOW_PARENT_COMPANIES_ONLY
        }
        onResetAllFilters={handleResetAllFilters}
        additionalColumnsForEdit={[
          {
            header: t("building_main"),
            entity: "building",
            id: "main_building",
            accessorKey: "main_building",
            accessorFn: (row: any) => {
              if (
                row.main_building !== null ||
                row.main.building !== undefined
              ) {
                return row.main_building.name;
              } else return null;
            },
            enableForCreate: true,
          },
        ]}
        additionalColumnsForCreate={[
          {
            header: `${t("buildings")} ${t("name")}`,
            accessorKey: "main_building.name",
            childEntityName: "main_building",
            id: "building_name",
            enableForCreate: true,
            required: true,
            errorMessage: t("field_required"),
          },
          {
            header: `${t("buildings")} ${t("building_street_house")}`,
            accessorKey: "main_building.street_house",
            childEntityName: "main_building",
            id: "street_house",
            enableForCreate: true,
            required: true,
            errorMessage: t("field_required"),
          },
          {
            header: `${t("buildings")} ${t("building_zip")}`,
            accessorKey: "main_building.zip",
            childEntityName: "main_building",
            id: "zip",
            enableForCreate: true,
            required: true,
            errorMessage: t("field_required"),
          },
          {
            header: `${t("buildings")} ${t("building_city")}`,
            accessorKey: "main_building.city",
            childEntityName: "main_building",
            id: "city",
            enableForCreate: true,
            required: true,
            errorMessage: t("field_required"),
          },
          {
            header: `${t("buildings")} ${t("building_contact_name")}`,
            accessorKey: "main_building.contact_name",
            childEntityName: "main_building",
            id: "contact_name",
            enableForCreate: true,
          },
          {
            header: `${t("buildings")} ${t("building_contact_email")}`,
            accessorKey: "main_building.contact_email",
            childEntityName: "main_building",
            id: "contact_email",
            enableForCreate: true,
          },
          {
            header: `${t("buildings")} ${t("building_contact_phone")}`,
            accessorKey: "main_building.contact_phone",
            childEntityName: "main_building",
            id: "contact_phone",
            enableForCreate: true,
          },
          {
            header: t("building_parking_situation"),
            accessorKey: "parking_situation",
            id: "parking_situation",
            enableForCreate: true,
          },
          {
            header: t("building_additional_information"),
            accessorKey: "additional_information",
            id: "additional_information",
            enableForCreate: true,
            textarea: true,
          },
        ]}
        columns={[
          {
            header: t("id"),
            accessorKey: "id",
            id: "id",
            enableForCreate: true,
            hideField: true,
          },
          {
            header: t("name"),
            accessorKey: "name",
            id: "name",
            enableForCreate: true,
            required: true,
            errorMessage: t("field_required"),
            accessorFn: (row: any) => {
              const companySubjectId = row.subject?.id;
              const companyName = row.name;
              const companyId = row.id;
              const isActive = row.is_active;

              if (!isActive) {
                return <span>{companyName}</span>;
              }

              return (
                <NavigateToSubjectOverviewLink
                  subjectId={companySubjectId || companyId}
                  label={companyName}
                />
              );
            },
          },

          {
            header: t("team"),
            accessorFn: (row: any) => {
              if (row.team !== null) {
                return row.team.name;
              } else return null;
            },
            id: "team",
            enableForCreate: true,
            entity: "team",
            required: true,
            errorMessage: t("field_required"),
            customColumnFilterId: "team",
            customFilterParameter: "__icontains",
          },
          {
            header: t("customer_id"),
            id: "customer_id",
            accessorKey: "customer_id",
            customSortingKey: "customer_id",
            sortDescFirst: false,

            accessorFn: (row: any) => {
              if (row.customer_id !== null) {
                return row.customer_id;
              } else return "-";
            },
          },
          // {
          //   header: t("service_level"),
          //   id: "service_level",
          //   accessorKey: "service_level",
          //   accessorFn: (row: any) => {
          //     if (row.service_level) {
          //       return t("service_level_" + row.service_level);
          //     }
          //   },
          //   filterVariant: "select",
          //   filterSelectOptions: [
          //     { value: "normal", label: t("service_level_normal") },
          //     { value: "key_account", label: t("service_level_key_account") },
          //     {
          //       value: "single_service",
          //       label: t("service_level_single_service"),
          //     },
          //     { value: "flatrate", label: t("service_level_flatrate") },
          //   ],
          //   enableForCreate: true,
          //   selectOptions: [
          //     {
          //       value: "normal",
          //       label: t("service_level_normal"),
          //     },
          //     {
          //       value: "key_account",
          //       label: t("service_level_key_account"),
          //     },
          //     {
          //       value: "single_service",
          //       label: t("service_level_single_service"),
          //     },
          //     {
          //       value: "flatrate",
          //       label: t("service_level_flatrate"),
          //     },
          //   ],
          // },
          {
            header: t("basic_care_billing_type"),
            id: "basic_care_billing_type",
            accessorKey: "basic_care_billing_type",
            accessorFn: (row: any) => {
              if (row.basic_care_billing_type) {
                return t(`billing_type_${row.basic_care_billing_type}`);
              }
              return null;
            },
            filterVariant: "select",
            setNoFilterParameter: true,
            filterSelectOptions: [
              { value: "standard", label: t("billing_type_standard") },
              { value: "flat_rate", label: t("billing_type_flat_rate") },
            ],
          },
          {
            header: t("support_type"),
            id: "support_type",
            accessorKey: "support_type",
            accessorFn: (row: any) => {
              if (row.support_type) {
                return t(`billing_type_${row.support_type}`);
              }
              return null;
            },
            filterVariant: "select",
            setNoFilterParameter: true,
            filterSelectOptions: [
              { value: "standard", label: t("support_type_standard") },
              { value: "key_account", label: t("support_type_key_account") },
            ],
          },
          {
            header: t("contract_category"),
            id: "contract_category",
            accessorKey: "contract_category",
            accessorFn: (row: any) => {
              if (row.contract_category) {
                return t(`contract_category_${row.contract_category}`);
              }
              return null;
            },
            filterVariant: "select",
            setNoFilterParameter: true,
            filterSelectOptions: [
              { value: "bm", label: t("contract_category_bm") },
              { value: "as", label: t("contract_category_as") },
              { value: "bm_as", label: t("contract_category_bm_as") },
            ],
          },
          {
            header: t("parent"),
            accessorFn: (row: any) => {
              if (row.parent !== null) {
                return row.parent.name;
              } else return null;
            },
            id: "parent",
            asyncDropDown: true,
            customColumnFilterId: "parent__name",
            customFilterParameter: "__icontains",
            customSortingKey: "parent_name",
            sortDescFirst: false,
            asyncEntity: "company",
            enableForCreate: true,
            customLabel: t("parent_company"),
            queryParameter: "companies",
            setCompanyId: true,
            validate: true,
            multiselect: false,
          },
          {
            header: t("customer_id_inherited"),
            id: "customer_id_inherited",
            accessorKey: "customer_id_inherited",
            enableColumnFilter: true,
            filterVariant: "select",
            setNoFilterParameter: true,
            filterSelectOptions: [
              { value: "true", label: t("yes") },
              { value: "false", label: t("no") },
            ],
            enableForCreate: true,
            checkBox: true,
            accessorFn: (row: any) => {
              if (row.customer_id_inherited) {
                return t("yes");
              } else {
                return t("no");
              }
            },
          },
          {
            header: t("2fa"),
            id: "requires_two_factor",
            accessorKey: "requires_two_factor",
            enableColumnFilter: true,
            filterVariant: "select",
            filterSelectOptions: [
              { value: "true", label: t("turned_on") },
              { value: "false", label: t("turned_off") },
            ],
            accessorFn: (row: any) => {
              return (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Tooltip
                    title={
                      row.hierarchy_enforced_two_factor
                        ? t("2fa_enforced_by_parent_company")
                        : ""
                    }
                    arrow
                    placement="top"
                  >
                    <span>
                      <Switch
                        checked={
                          row.requires_two_factor ||
                          row.hierarchy_enforced_two_factor
                        }
                        inputProps={{ "aria-label": "controlled" }}
                        onChange={(event: any) => handleTwoFactor(event, row)}
                        disabled={row.hierarchy_enforced_two_factor}
                      />
                    </span>
                  </Tooltip>
                </div>
              );
            },
          },
          {
            header: t("company_setup_status"),
            id: "draft_mode",
            accessorKey: "draft_mode",
            enableColumnFilter: true,
            filterVariant: "select",
            setNoFilterParameter: true,
            filterSelectOptions: [
              { value: "true", label: t("open") },
              { value: "false", label: t("completed") },
            ],
            accessorFn: (row: any) => {
              return (
                <div className={styles.companySetupStatusCell}>
                  {row.draft_mode ? (
                    <GenericButton
                      variant="outlined"
                      size="small"
                      className={styles.companySetupCompleteButton}
                      onClick={() => handleDraftMode(row)}
                    >
                      {t("company_setup_complete_action")}
                    </GenericButton>
                  ) : (
                    <span className={styles.companySetupCompleted}>
                      <CheckCircleIcon color="success" fontSize="small" />
                      {t("completed")}
                    </span>
                  )}
                </div>
              );
            },
          },
          {
            header: t("building_main"),
            accessorKey: "main_building",
            id: "main_building",
            customColumnFilterId: "main_building__name",
            customFilterParameter: "__icontains",
            customSortingKey: "main_building_name",
            sortDescFirst: false,
            accessorFn: (row: any) => {
              if (
                row.main_building &&
                (row.main_building !== null || row.main_building !== undefined)
              ) {
                const building = row.main_building;
                return (
                  <>
                    <span className={styles.buildingName}>{building.name}</span>
                    <br />
                    <span>
                      {building.street_house}, {building.zip} {building.city}
                    </span>
                  </>
                );
              }
            },
          },
          ...getRecordMetadataColumns(t, [
            "created_on",
            "created_by",
            "modified_on",
            "modified_by",
          ]),
        ]}
      />
      {errorModal}
      {setupIncompleteModal}
      <GenericNotification
        message={showNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />

      <TodoCreateEditModal
        open={modalTodoOpen}
        onClose={() => {
          setModalTodoOpen(false);
          setShowNotification(false);
          setNotificationMessage("");
        }}
        onSuccess={() => {
          setModalTodoOpen(false);
          setResetRowSeletion((prev: any) => !prev);
          setNotificationMessage(t("todo_successfully_created_for_companies"));
          setNotificationVariant("success");
          setShowNotification(true);
        }}
        isBatchMode={true}
        selectedCompanies={selectedCompaniesForTodo}
        selectedTableRows={selectedRowsForEmail}
      />

      <Modal
        open={emailResultsModalOpen}
        hideCancelButton={true}
        onSubmit={handleCloseEmailResultsModal}
        submitButtonText={t("close")}
        title={t("overview")}
        buttonPlacement="flex-end"
      >
        {!emailResultsModalContent ? (
          <>
            <LinearProgress />
            <span className={styles.uploadModalText}>
              {t("wait_for_email_send_progress")}
            </span>
          </>
        ) : (
          emailResultsModalContent
        )}
      </Modal>
      {selectedCompanyForHierarchy && (
        <CompanyHierarchyModal
          open={hierarchyModalOpen}
          onClose={() => {
            setHierarchyModalOpen(false);
            setSelectedCompanyForHierarchy(null);
          }}
          subjectId={selectedCompanyForHierarchy.subjectId}
          selectedCompanyId={selectedCompanyForHierarchy.companyId}
          selectedSubjectId={selectedCompanyForHierarchy.subjectId}
        />
      )}
      <Modal
        open={csvUploadModalOpen}
        onClose={() => setCsvUploadModalOpen(false)}
        title={t("csv_import")}
        hideDialogActions={true}
        disableCancelButton={csvUploadInProgress}
        maxWidth="40rem"
      >
        <FileUploadInput
          multiple={false}
          name="company_csv"
          folderId="company"
          onCancel={() => setCsvUploadModalOpen(false)}
          customUploadHandler={handleCsvUpload}
          accept={[".csv"]}
          onUploadStateChange={setCsvUploadInProgress}
          handleUploadSuccess={(modalOpen: boolean) => {
            if (modalOpen) {
              setCsvUploadModalOpen(false);
            }
          }}
        />
      </Modal>
      <Modal
        open={entriesModalOpen}
        onSubmit={() => setEntriesModalOpen(false)}
        title={t("overview")}
        submitButtonText={t("confirm")}
        buttonPlacement="flex-end"
        hideCancelButton
      >
        {entriesModalContent}
      </Modal>

      <CompanyBatchEmailModal
        modalOpen={modalEmailOpen}
        selectedTableRows={selectedRowsForEmail}
        handleSubmitFormData={handleSendEmail}
        onClose={handleSendEmailModalClose}
      />
      <CreateDocumentModal
        modalOpen={createDocumentModalOpen}
        onClose={handleCreateDocumentModalClose}
        selectedTableRows={selectedRowsForEmail}
        handleSubmitFormData={handleCreateDocument}
      />
      <CreateTrackedServiceModal
        modalOpen={createTrackedServiceModalOpen}
        selectedTableRows={selectedRowsForEmail}
        onClose={handleCreateTrackedServiceModalClose}
        handleSubmitFormData={handleCreatetrackedServiceEntry}
      />
      <CompanyBatchUploadModal
        modalOpen={modalUploadOpen}
        onClose={handleBatchUploadModalClose}
        selectedTableRows={selectedRowsForEmail}
        handleSubmitFormData={handleBatachUpload}
      />
      {companyAdditionalGroups && (
        <CompanyAdditionalGroupAssignments
          companyId={companyAdditionalGroups.id}
          assignments={companyAdditionalGroups.assignments}
          onClose={() => setCompanyAdditionalGroups(null)}
          onSaved={() => {
            setCompanyAdditionalGroups(null);
            setRefreshTable((value) => !value);
          }}
        />
      )}
    </RestrictedPage>
  );
};

export default CompanyList;
